import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { API } from '../config/api';
import { ArrowLeft, Clock, Eye, User, Newspaper } from 'lucide-react';

export default function ArticlePage() {
  const { articleId } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        const res = await fetch(`${API}/news/articles/${articleId}`);
        if (res.ok) setArticle(await res.json());
      } catch (err) {
        console.error('Failed to fetch article:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchArticle();
  }, [articleId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="glass-card border-border p-8 text-center max-w-md">
          <Newspaper className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
          <h2 className="font-bold mb-2">Article Not Found</h2>
          <Button variant="outline" onClick={() => navigate('/soundstage-spotlight')}>Back to Spotlight</Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="article-page">
      <div className="max-w-3xl mx-auto px-4 py-6">
        {/* Back */}
        <Button variant="ghost" size="sm" className="mb-4 gap-1 text-muted-foreground" onClick={() => navigate('/soundstage-spotlight')}>
          <ArrowLeft className="w-4 h-4" /> Back to Spotlight
        </Button>

        {/* Hero image */}
        {article.thumbnail_url && (
          <div className="rounded-xl overflow-hidden mb-6 aspect-video">
            <img src={article.thumbnail_url} alt="" className="w-full h-full object-cover" />
          </div>
        )}

        {/* Meta */}
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <Badge variant="outline" className="text-xs">{article.category}</Badge>
          {article.is_sponsored && <Badge className="bg-amber-500/80 text-xs">Sponsored</Badge>}
          {article.pinned && <Badge className="bg-green-500/20 text-green-300 text-xs">Pinned</Badge>}
        </div>

        <h1 className="text-2xl sm:text-3xl font-heading font-black mb-3">{article.title}</h1>

        <div className="flex items-center gap-4 text-sm text-muted-foreground mb-8">
          <span className="flex items-center gap-1"><User className="w-3.5 h-3.5" /> {article.author_name}</span>
          <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {new Date(article.created_at).toLocaleDateString()}</span>
          <span className="flex items-center gap-1"><Eye className="w-3.5 h-3.5" /> {article.views || 0} views</span>
        </div>

        {/* Article body */}
        <div
          className="prose prose-invert prose-sm max-w-none leading-relaxed text-foreground/90"
          style={{ whiteSpace: 'pre-wrap' }}
          data-testid="article-body"
        >
          {article.body}
        </div>

        {/* Sponsor footer */}
        {article.is_sponsored && article.sponsor_name && (
          <Card className="glass-card border-amber-500/20 p-4 mt-8 text-center">
            <p className="text-xs text-muted-foreground">Sponsored by <span className="font-semibold text-amber-300">{article.sponsor_name}</span></p>
          </Card>
        )}
      </div>
    </div>
  );
}
